import { createFileRoute } from "@tanstack/react-router";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";

import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";
import { SITE_URL } from "@/lib/locale";

const faqs: { q: string; a: string }[] = [
  {
    q: "Was kostet es, einen Shopify Shop erstellen zu lassen?",
    a: "Die Spannweite ist groß und hängt von Sortiment, Migrationstiefe, Anzahl der benötigten Apps, Schnittstellen zu ERP und Versand sowie dem Grad individueller Anpassungen ab. Pauschalpreise wären unseriös. Eine belastbare Größenordnung nennen wir nach dem ersten Strategiegespräch, in dem wir Ihre konkrete Ausgangslage einordnen.",
  },
  {
    q: "Wie lange dauert die Entwicklung eines Shopify-Stores?",
    a: "Ein schlanker Store mit Standard-Theme, ohne Migration und mit überschaubarem Sortiment lässt sich in wenigen Wochen umsetzen. Migrationen, ERP- und Warenwirtschafts-Schnittstellen sowie individuelle Anpassungen verlängern den Zeitrahmen entsprechend. Wir strukturieren Projekte in vier Phasen (siehe Sektion Vorgehen) und terminieren realistisch, nicht in Wochenend-Versprechen.",
  },
  {
    q: "Was ist der Unterschied zwischen Shopify und Shopify Plus?",
    a: "Shopify Plus ist der Enterprise-Tarif mit erweiterter Checkout-Anpassung (Checkout Extensibility, Scripts/Functions), höheren API-Limits, Automatisierungen wie Shopify Flow sowie Multi-Store-Setups. Plus lohnt sich, wenn die Anforderungen an Checkout-Logik, Internationalisierung oder Volumen über das hinausgehen, was Standard-Shopify abbildet.",
  },
  {
    q: "Können Sie meinen bestehenden Shop auf Shopify migrieren?",
    a: "Ja. Wir übernehmen Migrationen von WooCommerce, Shopware und Eigenlösungen inklusive Produktdaten, Kunden, Bestellungen sowie URL-Mapping und 301-Weiterleitungen. Ziel ist, dass weder Daten noch bestehende Suchmaschinen-Rankings verloren gehen.",
  },
  {
    q: "Sind Sie Shopify Partner?",
    a: "Calvarius ist eine Digitalagentur mit system-übergreifender E-Commerce-Erfahrung seit 2013 und arbeitet operativ mit Shopify, Shopware und WooCommerce. Eine formale Shopify-Partner-Zertifizierung führen wir nicht. Unser Anspruch ist technische Substanz und ehrliche System-Beratung, nicht ein Badge.",
  },
  {
    q: "Übernehmen Sie auch die laufende Betreuung meines Shopify-Stores?",
    a: "Ja. Wir bieten Shopify-Betreuung im Monatsmodell: laufende technische Pflege, App-Management, Performance-Optimierung und Weiterentwicklung. Für Händler, die einen verlässlichen technischen Partner statt punktueller Einzelaufträge wollen.",
  },
  {
    q: "Brauche ich für Shopify viele kostenpflichtige Apps?",
    a: "Nein. Jede App kostet Ladezeit und monatliche Gebühr. Wir wählen gezielt aus, was wirklich gebraucht wird, und bauen schlanke Funktionen dort lieber selbst, wo es technisch und wirtschaftlich sinnvoll ist. App-Wildwuchs ist eine der häufigsten Ursachen für langsame und teure Shopify-Stores.",
  },
  {
    q: "Arbeiten Sie auch mit Händlern außerhalb Mainfrankens?",
    a: "Ja. Unser Sitz ist in Karlstadt am Main, die Zusammenarbeit läuft ortsunabhängig — bundesweit und im DACH-Raum. Die meisten Projekte werden remote betreut, Vor-Ort-Termine sind bei Bedarf möglich.",
  },
];

export const Route = createFileRoute("/leistungen/onlineshop-entwicklung_/shopify-agentur")({
  head: () => {
    const h = pageHead("shopifyAgentur", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(
          serviceSchema({
            name: "Shopify-Agentur",
            description:
              "Shopify-Agentur für Händler: Shopify-Shop erstellen lassen, Migration auf Shopify, Betreuung, Apps und Schnittstellen-Integration. System-übergreifend beraten, sauber gebaut.",
            url: "/leistungen/onlineshop-entwicklung/shopify-agentur",
            serviceType: "Shopify-Agentur / Shopify-Entwicklung",
            areaServed: ["DE"],
          }),
        ),
        jsonLdScript(
          breadcrumbSchema([
            { name: "Start", path: "/" },
            { name: "Leistungen", path: "/leistungen" },
            { name: "Onlineshop-Entwicklung", path: "/leistungen/onlineshop-entwicklung" },
            { name: "Shopify-Agentur", path: "/leistungen/onlineshop-entwicklung/shopify-agentur" },
          ]),
        ),
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
          url: `${SITE_URL}/leistungen/onlineshop-entwicklung/shopify-agentur`,
        }),
      ],
    };
  },
  component: ShopifyAgenturPage,
});

const phases = [
  { num: "01", title: "System-Entscheid & Datenmodell", body: "Wir klären, ob Shopify das richtige System ist, und strukturieren Sortiment, Preislogik, Varianten und Produktdaten so, dass sie in Shopify und im Marketing sauber laufen. Typisch 3–5 Tage." },
  { num: "02", title: "Store & Checkout", body: "Aufbau bzw. Anpassung von Theme und Storefront, performante Kategorie- und Produktseiten, ein kurzer und vertrauenswürdiger Checkout. Apps nur dort, wo sie wirklich gebraucht werden. Typisch 2–4 Wochen." },
  { num: "03", title: "Apps, Schnittstellen & Integration", body: "Anbindung von Payment, Versand, Warenwirtschaft und ERP, Einrichtung rechtssicherer Pflichttexte und Steuerregeln sowie E-Commerce-Tracking, Produktfeeds und Consent-Management — strukturell verankert, nicht nachträglich aufgesetzt." },
  { num: "04", title: "Go-live, Skalierung & Pflege", body: "Tests inkl. Checkout- und Lastszenarien, sauberer Go-live, danach Begleitung bei Sortimentserweiterung, Performance, neuen Verkaufskanälen und laufender technischer Pflege. Auf Wunsch als Shopify-Betreuung im Monatsmodell." },
];

const capabilities = [
  { name: "Theme- & Storefront-Anpassung", detail: "Liquid, Sections, Online Store 2.0" },
  { name: "Shopify Plus", detail: "Checkout Extensibility, Scripts/Functions" },
  { name: "App-Integration & -Konfiguration", detail: "gezielt, performance-bewusst" },
  { name: "Migration auf Shopify", detail: "Daten, URLs, Weiterleitungen" },
  { name: "Payment- & Steuer-Integration", detail: "Zahlarten, Steuerregeln, Rechnungslogik" },
  { name: "ERP-, WaWi- & Versand-Schnittstellen", detail: "automatisiert statt manuell" },
  { name: "E-Commerce-Tracking & Feeds", detail: "serverseitig, consent-konform" },
  { name: "Performance-Optimierung", detail: "Ladezeit auch bei großem Katalog" },
  { name: "Internationalisierung", detail: "Shopify Markets, Sprachen, Währungen" },
];


const mechanics = [
  { title: "Checkout & Conversion", body: "Jeder unnötige Schritt kostet Umsatz. Wir halten den Checkout kurz, klar und vertrauenswürdig — im Rahmen dessen, was Shopify bzw. Shopify Plus an Checkout-Anpassung zulässt." },
  { title: "App-Auswahl statt App-Wildwuchs", body: "Jede App kostet Ladezeit und monatliche Gebühr. Wir wählen gezielt aus und bauen, wo sinnvoll, lieber schlank selbst." },
  { title: "Produktdaten & Feeds", body: "Strukturierte Produktdaten als Basis für Shop, Shopping-Anzeigen und Marketing. Verknüpfung mit Delta möglich.", link: { label: "Verknüpfung mit Delta →", href: "/leistungen/delta" } },
  { title: "Schnittstellen", body: "Payment, Versand, Warenwirtschaft und ERP greifen automatisch ineinander statt manueller Nachpflege." },
  { title: "E-Commerce-Tracking", body: "Purchase-Events, serverseitiges Tracking, Produktfeeds und Consent-Management von Anfang an.", link: { label: "Mehr zu Tracking & Datenbasis →", href: "/leistungen/tracking-datenbasis" } },
  { title: "Rechtssicherheit", body: "Widerrufsbelehrung, Preisangabenverordnung, Pflichtangaben und Steuerregeln korrekt umgesetzt." },
];

const services4 = [
  {
    title: "Shopify Shop erstellen lassen",
    body: "Konzeption und Umsetzung eines neuen Shopify- oder Shopify-Plus-Stores — vom Datenmodell über Theme und Checkout bis zur ersten sauberen Tracking- und Feed-Basis. Für Händler, die mit Shopify starten und es von Beginn an richtig aufsetzen wollen.",
  },
  {
    title: "Migration auf Shopify",
    body: "Umzug von einem bestehenden System (z. B. WooCommerce, Shopware oder einer Eigenlösung) auf Shopify — inklusive Produktdaten, Kunden, Bestellungen, URLs und Weiterleitungen, damit weder Daten noch Suchmaschinen-Rankings verloren gehen.",
  },
  {
    title: "Shopify-Betreuung & Weiterentwicklung",
    body: "Laufende technische Pflege, App-Management, Performance-Optimierung und Weiterentwicklung bestehender Shopify-Stores — auch Shopify Plus. Für Händler, die einen verlässlichen technischen Partner statt punktueller Einzelaufträge wollen.",
  },
  {
    title: "Shopify-Entwicklung & Schnittstellen",
    body: "Individuelle Anpassungen jenseits des Theme-Editors: Liquid-Templating, maßgeschneiderte Funktionen, App- und API-Integrationen, ERP- und Warenwirtschafts-Anbindung. Dort, wo Standard-Apps an Grenzen stoßen.",
  },
];

function ShopifyAgenturPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Shopify-Agentur"
          headline={<>Shopify-Agentur für Händler, die ihren Store als <em>verkaufendes System</em> verstehen.</>}
          lead="Wir entwickeln, migrieren und betreuen Shopify-Shops — von der Theme-Anpassung über App- und Schnittstellen-Integration bis zum sauberen E-Commerce-Tracking. Für Händler, die einen Shopify Shop erstellen lassen wollen, der nicht nur Produkte zeigt, sondern verkauft."
          metaStrip={[
            "Shopify & Shopify Plus",
            "Theme & Storefront",
            "App- & Schnittstellen-Integration",
            "Migration auf Shopify",
            "E-Commerce-Tracking als Standard",
          ]}
          primaryCta={{
            eyebrow: "Kostenfrei · circa 30 Minuten · keine Verpflichtung",
            label: "Strategiegespräch anfragen",
            to: "/kontakt",
            search: { topic: "shopify" },
          }}
          secondaryCta={{
            label: "Oder: zur übergeordneten Onlineshop-Entwicklung",
            to: "/leistungen/onlineshop-entwicklung",
          }}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "Onlineshop-Entwicklung", to: "/leistungen/onlineshop-entwicklung" },
            { label: "Shopify-Agentur" },
          ]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Shopify ist schnell aufgesetzt. Schwer ist, dass er auch <em>verkauft.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Wer eine Shopify-Agentur sucht, hat meist eine von drei Ausgangslagen: ein neuer Shop soll entstehen, ein bestehendes System soll auf Shopify migriert werden, oder ein laufender Shopify-Store leistet weniger, als er sollte. Allen dreien gemeinsam ist ein Irrtum, der teuer wird: dass Shopify, weil es schnell installiert ist, auch schnell verkauft. Das Aufsetzen ist der einfache Teil. Die Mechanik dahinter entscheidet.</p>
              <p className="mb-5">Shopify nimmt einem Hosting, Updates und Sicherheit ab — das ist die Stärke des Systems. Was es einem nicht abnimmt: einen Checkout, der nicht unnötig abbricht, Produktdaten, die strukturiert statt in Tabellen leben, Apps, die nicht die Ladezeit ruinieren, Schnittstellen zu Warenwirtschaft, ERP und Versand, die automatisch greifen, und ein E-Commerce-Tracking, das Purchase-Events und Produktfeeds von Anfang an sauber erfasst. Genau hier arbeiten wir als Shopify-Entwickler — nicht am Theme-Klick, sondern an dem, was darunter über Umsatz entscheidet.</p>
              <p>Shopify ist nicht für jeden Händler das richtige System. Für markenorientierte Händler mit überschaubarem bis mittlerem Sortiment, hohem Anspruch an Gestaltung und schnellem Go-live ist es oft die beste Wahl. Wer hochkomplexe B2B-Logik, tief individualisierte Prozesse oder sehr große Sortimente mit Sonderregeln braucht, ist mit Shopware häufig besser bedient. Wir sagen das offen — auch wenn es bedeutet, von Shopify abzuraten. Welches System zu Ihrem Geschäftsmodell passt, klären wir vor der ersten Zeile Code.</p>
            </div>
            <div className="mt-10 rounded-lg border-l-4 border-lime bg-bg-subtle p-5 text-[15px] leading-relaxed text-ink-2">
              Sie sind unsicher zwischen Shopify und Shopware?{" "}
              <a href="#systemvergleich" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">
                Zum Systemvergleich →
              </a>{" "}
              · Oder die system-übergreifende Sicht:{" "}
              <a href="/leistungen/onlineshop-entwicklung" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">
                Onlineshop-Entwicklung →
              </a>
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          variant="tinted"
          number="03"
          kicker="Vorgehen"
          headline={<>Vom <em>System-Entscheid</em> zum laufenden Shopify-Store.</>}
          caption="Wie wir Shopify-Projekte strukturieren — realistisch terminiert, nicht in Wochenend-Versprechen."
        >
          <ProcessFlow steps={phases} />
        </Section>

        <Section
          number="04"
          kicker="Leistungen"
          headline={<>Was wir als <em>Shopify-Agentur</em> übernehmen.</>}
          caption="Vier Schwerpunkte — je nach Ausgangslage einzeln oder kombiniert."
        >
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              {services4.map((s) => (
                <div key={s.title} className="rounded-lg border border-rule bg-white p-7">
                  <h3 className="mb-3 font-display text-xl text-ink">{s.title}</h3>
                  <p className="text-[15px] leading-relaxed text-ink-2">{s.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <CTABlock
                headline={<>Welche Ausgangslage trifft auf <em>Sie</em> zu?</>}
                body="Ob neuer Shopify-Shop, Migration auf Shopify oder ein bestehender Store, der mehr leisten soll — wir klären es im Strategiegespräch."
                primary={{ label: "Strategiegespräch anfragen", to: "/kontakt", search: { topic: "shopify" } }}
              />
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="05"
          kicker="Mechanik"
          headline={<>Was einen Shopify-Store zum <em>verkaufenden System</em> macht.</>}
          caption="Die Themen, die über Umsatz entscheiden — und die der Theme-Editor allein nicht löst."
        >
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              {mechanics.map((m) => (
                <div key={m.title} className="rounded-lg border border-rule bg-white p-7">
                  <h3 className="mb-3 font-display text-xl text-ink">{m.title}</h3>
                  <p className="text-[15px] leading-relaxed text-ink-2">{m.body}</p>
                  {m.link && (
                    <a href={m.link.href} className="mt-4 inline-flex items-center font-mono text-xs uppercase tracking-wider text-ink-2 hover:text-lime-deep">
                      {m.link.label}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="06" kicker="Capabilities" headline={<>Womit wir Shopify-Stores <em>verkaufsfähig</em> machen.</>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section
          id="systemvergleich"
          variant="tinted"
          number="07"
          kicker="Systemvergleich"
          headline={<>Shopify oder Shopware — welches System für <em>welchen Händler.</em></>}
          caption="Eine ehrliche Einordnung statt Plattform-Werbung."
        >
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-lg border border-rule bg-white p-7">
                <h3 className="mb-4 font-display text-xl text-ink">Shopify ist meist richtig für:</h3>
                <ul className="space-y-3 text-[15px] leading-relaxed text-ink-2">
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>markenorientierte Händler mit eigenem Sortiment und hohem Anspruch an Gestaltung und Look &amp; Feel</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>Händler, die schnell live gehen wollen, ohne eigenes Server- und Wartungsthema</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>kleine bis mittlere Sortimente mit überschaubarer Variantenlogik</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>klare, weitgehend standardisierbare Verkaufsprozesse</span></li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-7">
                <h3 className="mb-4 font-display text-xl text-ink">Shopware ist meist richtig für:</h3>
                <ul className="space-y-3 text-[15px] leading-relaxed text-ink-2">
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>sehr große Sortimente oder komplexe Variantenstrukturen</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>tief individualisierte oder regelbasierte Prozesse (B2B, Staffelpreise, Freigaben)</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>stark internationale/multilinguale Setups mit Sonderanforderungen</span></li>
                  <li className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>Händler, die volle Kontrolle über den Quellcode brauchen</span></li>
                </ul>
              </div>
            </div>
            <p className="mt-8 max-w-2xl text-[15px] leading-relaxed text-ink-2">
              Beide Systeme sind ausgereift. Die Wahl entscheidet sich nicht an einer Feature-Liste, sondern an Ihrem Geschäftsmodell, Ihrem Sortiment und Ihren Prozessen. Wir klären das vorab — und sagen auch, wenn weder das eine noch das andere die beste Lösung ist.{" "}
              <a href="/leistungen/onlineshop-entwicklung" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">
                Zur system-übergreifenden Onlineshop-Entwicklung →
              </a>
            </p>
          </RevealOnScroll>
        </Section>

        <LogoReferences locale="de" number="08" />

        <Section number="09" kicker="FAQ" headline={<>Häufige Fragen zur <em>Shopify-Agentur.</em></>}>
          <RevealOnScroll>
            <div className="divide-y divide-rule rounded-lg border border-rule bg-white">
              {faqs.map((f) => (
                <details key={f.q} className="group p-6">
                  <summary className="cursor-pointer list-none font-display text-lg text-ink marker:hidden">
                    <span className="mr-3 text-cyan-deep">+</span>
                    {f.q}
                  </summary>
                  <p className="mt-4 pl-6 text-[15px] leading-relaxed text-ink-2">{f.a}</p>
                </details>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <RelatedServices
          locale="de"
          fromId="shopify-agentur"
          number="10"
          items={relatedBySource["shopify-agentur"]}
        />

        <Section number="11" kicker="Engagement">
          <CTABlock
            headline={<>Lassen Sie uns über Ihren <em>Shopify-Store</em> sprechen.</>}
            body="Ob neuer Shopify-Shop, Migration auf Shopify oder ein bestehender Store, der mehr leisten soll — wir klären im Strategiegespräch, was zu Ihrem Sortiment, Ihrer Logistik und Ihrem Geschäftsmodell passt. Konkret, ehrlich und ohne Verkaufsdruck. Wenn Shopify nicht das richtige System ist, sagen wir auch das."
            primary={{ label: "Strategiegespräch anfragen", to: "/kontakt", search: { topic: "shopify" } }}
            secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
